import api from '../../../config/axios';

/**
 * Livello di accesso API per la generazione batch SiSketron.
 *
 * - Gli endpoint di sola lettura legacy (`/generator/...`) vengono riusati per
 *   popolare prodotti, moduli e scheletri, come previsto dal contratto backend.
 * - Tutto ciò che riguarda piano, batch e risultati passa esclusivamente da
 *   `/generator-batch`.
 *
 * Ogni metodo accetta un `signal` opzionale (AbortController) per annullare le
 * richieste diventate obsolete.
 */

const BATCH = '/generator-batch';

// --- Risorse legacy di sola lettura -----------------------------------------

export const fetchResources = (signal) =>
  api.get('/generator/resources', { signal }).then((r) => r.data);

export const fetchModules = (productId, signal) =>
  api.get(`/generator/modules/${productId}`, { signal }).then((r) => r.data);

export const fetchSkeletonsByRoot = (root, signal) =>
  api
    .get(`/generator/skeletons/${encodeURIComponent(root)}`, { signal })
    .then((r) => r.data);

// --- Batch: macrofunzioni recenti -------------------------------------------

/**
 * @param {Object} filters { since, until, productId, moduleIds, mine, limit, offset }
 */
export const fetchRecent = (filters = {}, signal) => {
  const params = new URLSearchParams();
  const { since, until, productId, moduleIds, mine, limit, offset } = filters;
  if (since) params.append('since', since);
  if (until) params.append('until', until);
  if (productId != null && productId !== '') params.append('productId', productId);
  if (Array.isArray(moduleIds) && moduleIds.length > 0) {
    params.append('moduleIds', moduleIds.join(','));
  }
  if (mine) params.append('mine', 'true');
  if (limit != null) params.append('limit', limit);
  if (offset != null) params.append('offset', offset);
  const qs = params.toString();
  return api
    .get(`${BATCH}/recent${qs ? `?${qs}` : ''}`, { signal })
    .then((r) => r.data);
};

/**
 * Dettaglio lazy delle modifiche di una macrofunzione.
 * Da chiamare SOLO all'espansione di una scheda (mai N+1 al caricamento).
 * @param {number|string} macroFunctionId
 * @param {Object} range { since, until, limit }
 */
export const fetchRecentChanges = (macroFunctionId, range = {}, signal) => {
  const params = new URLSearchParams();
  const { since, until, limit } = range;
  if (since) params.append('since', since);
  if (until) params.append('until', until);
  if (limit != null) params.append('limit', limit);
  const qs = params.toString();
  // encodeURIComponent anche se numerico, per coerenza difensiva.
  const id = encodeURIComponent(String(macroFunctionId));
  return api
    .get(`${BATCH}/recent/${id}/changes${qs ? `?${qs}` : ''}`, { signal })
    .then((r) => r.data);
};

// --- Batch: piano e creazione -----------------------------------------------

/**
 * Body comune a piano e creazione:
 *   {
 *     macroFunctionIds: number[],
 *     skeletons: {root, path}[],
 *     logLevel?: 'DEBUG'|'INFO'|'WARNING'|'NONE',
 *     regenerationMode?: 'OUTDATED_ONLY'|'ALL'   // default lato server: ALL
 *   }
 */
export const requestPlan = (body, signal) =>
  api.post(`${BATCH}/plan`, body, { signal }).then((r) => r.data);

export const createBatch = (body, signal) =>
  api.post(`${BATCH}/batches`, body, { signal }).then((r) => r.data);

export const fetchBatch = (batchId, signal) =>
  api.get(`${BATCH}/batches/${batchId}`, { signal }).then((r) => r.data);

export const fetchBatchList = (signal) =>
  api.get(`${BATCH}/batches`, { signal }).then((r) => r.data);

export const cancelBatch = (batchId, signal) =>
  api.post(`${BATCH}/batches/${batchId}/cancel`, null, { signal }).then((r) => r.data);

// --- Batch: log, file, preview, archivio ------------------------------------

export const fetchTaskLog = (batchId, taskId, maxBytes, signal) => {
  const params = maxBytes ? `?maxBytes=${maxBytes}` : '';
  return api
    .get(`${BATCH}/batches/${batchId}/tasks/${taskId}/log${params}`, { signal })
    .then((r) => r.data);
};

export const fetchFiles = (batchId, signal) =>
  api.get(`${BATCH}/batches/${batchId}/files`, { signal }).then((r) => r.data);

export const fetchFilePreview = (batchId, fileId, signal) =>
  api
    .get(`${BATCH}/batches/${batchId}/files/${fileId}/preview`, { signal })
    .then((r) => r.data);

/**
 * Scarica l'archivio ZIP del batch e forza il download nel browser.
 */
export const downloadArchive = (batchId) =>
  api
    .get(`${BATCH}/batches/${batchId}/archive`, { responseType: 'blob' })
    .then((response) => {
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `generator-batch-${batchId}.zip`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    });
